<template>
  <div class="c-header-container">
    <!-- 公告 -->
    <div class="c-header-summary">
      <div class="c-header-summary__notice">
        <SvgIcon width="16px" height="16px" name="notice" class="notice-icon" />
        <span class="notice-text">{{ topNoticeText }}</span>
      </div>
      <div class="c-header-summary__right">
        <div class="balance-text" @click="handleRechargeClick">
          <span class="balance-unit">INR:</span>
          <span class="balance-amount">{{ balanceAmountText }}</span>
        </div>
        <div type="button" class="history-btn" @click="goToHistory">
          <SvgIcon width="16px" height="20px" name="orders" />
        </div>
      </div>
    </div>
    <!-- tab页面 -->
    <div v-if="isTopTabLayout" class="c-top-tab-header">
      <div class="c-top-tab-header__tabs">
        <button v-for="tab in topTabs" :key="tab.name" type="button" class="top-tab-item"
          :class="{ 'is-active': activeTopTabName === tab.name }" @click="onSwitchTopTab(tab.name)">
          <img class="top-tab-icon" :src="activeTopTabName === tab.name ? tab.activeIcon : tab.icon" :alt="tab.label" />
          <span class="top-tab-label">{{ tab.label }}</span>
        </button>
      </div>
    </div>
    <!-- 普通头部 -->
    <div v-else class="c-header" :class="{ 'c-header--out-layout': isOutLayout }">
      <div class="c-header-left">
        <div v-if="showBack" class="c-header-back" @click="handleBack">
          <van-icon name="arrow-left" class="c-header-icon" />
        </div>
        <div class="c-header-titles">
          <slot name="title">
            <div class="c-header-main" :class="{ 'c-header-main--large': !hasSubtitle }">
              <span class="c-header-title-text">{{ displayTitle }}</span>
              <!-- 状态标签 -->
              <span v-if="showStatusBadge || computedShowStatus" class="c-header-status" :class="statusClass">
                <span v-if="computedStatusType === 'live' || computedStatusType === 'completed'"
                  class="c-header-status-dot" :class="{
                    'c-header-status-dot--live': computedStatusType === 'live',
                    'c-header-status-dot--completed': computedStatusType === 'completed',
                  }"></span>
                {{ displayStatusText }}
              </span>
            </div>
            <div v-if="hasSubtitle" class="c-header-sub">
              <slot name="subtitle">
                {{ subtitle }}
              </slot>
            </div>
          </slot>
        </div>
      </div>
      <div class="c-header-right">
        <slot name="right">
          <div v-if="showPts" class="c-header-pts" @click="handlePtsClick">
            <SvgIcon name="pts" width="22px" color class="c-header-pts-icon" />
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, useSlots, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { MatchStatusMap } from '@/views/contests/matchesType.js'
import { routeRechargePage } from 'dad-h5-common-js'
import { useStore } from '@/stores'
import frameIcon from '@/assets/svg/tabbar/Frame.svg'
import frameActiveIcon from '@/assets/svg/tabbar/Frame-active.svg'
import subtractIcon from '@/assets/svg/tabbar/Subtract.svg'
import subtractActiveIcon from '@/assets/svg/tabbar/Subtract-active.svg'

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  subtitle: {
    type: String,
    default: '',
  },
  showBack: {
    type: Boolean,
    default: true,
  },
  showPts: {
    type: Boolean,
    default: false,
  },
  ptsUrl: {
    type: String,
    default: '/rules/detail',
  },
  isOutLayout: {
    type: Boolean,
    default: false,
  },
  // 新增：状态类型
  statusType: {
    type: String,
    default: '', // 'live' | 'completed' | 'scheduled' | ''
  },
  // 新增：是否显示状态标签
  showStatusBadge: {
    type: Boolean,
    default: false,
  },
  // 新增：自定义状态文本
  statusText: {
    type: String,
    default: '',
  },
  // 新增：比赛状态码（用于自动映射文本）
  matchStatus: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['back'])

const route = useRoute()
const router = useRouter()
const store = useStore()
const topNoticeText = 'IPL is ON · Win real cash now'
const topTabs = [
  { name: 'Home', label: 'Home', icon: frameIcon, activeIcon: frameActiveIcon },
  { name: 'Matches', label: 'My Matches', icon: subtractIcon, activeIcon: subtractActiveIcon },
]
const isTopTabLayout = computed(() => route.meta?.layout === 'top-tab')
const activeTopTabName = computed(() => {
  return topTabs.some(tab => tab.name === route.name) ? route.name : 'TopHome'
})
const userBalance = computed(() => {
  return Number(store.USERINFO?.totalBalanceDiamond ?? store.USER_BALANCE ?? 0) || 0
})
const balanceAmountText = computed(() => {
  return userBalance.value
  // return userBalance.value.toLocaleString('en-IN', {
  //   minimumFractionDigits: 2,
  //   maximumFractionDigits: 2,
  // })
})
const isFetchingUserInfo = ref(false)

const displayTitle = computed(() => {
  return props.title || route.meta?.title || ''
})

const slots = useSlots()

const hasSubtitle = computed(() => {
  return !!props.subtitle || !!slots.subtitle
})
const showPts = computed(() => {
  return props.showPts || route.meta?.showPts || false
})
// 计算是否显示状态标签
const computedShowStatus = computed(() => {
  return props.showStatusBadge || !!props.statusType || props.matchStatus > 0
})

// 计算状态类型
const computedStatusType = computed(() => {
  if (props.statusType) return props.statusType
  // 根据matchStatus自动判断
  switch (props.matchStatus) {
    case 1:
      return 'scheduled'
    case 2:
      return 'completed'
    case 3:
      return 'live'
    default:
      return ''
  }
})

// 计算状态样式类名
const statusClass = computed(() => {
  return {
    'c-header-status--live': computedStatusType.value === 'live',
    'c-header-status--completed': computedStatusType.value === 'completed',
    'c-header-status--scheduled': computedStatusType.value === 'scheduled',
  }
})

// 计算显示的状态文本
const displayStatusText = computed(() => {
  if (props.statusText) return props.statusText
  // 根据matchStatus自动映射
  if (props.matchStatus > 0) {
    return MatchStatusMap[props.matchStatus] || ''
  }
  // 根据statusType返回默认文本
  switch (computedStatusType.value) {
    case 'live':
      return 'Live'
    case 'completed':
      return 'Completed'
    case 'scheduled':
      return 'Scheduled'
    default:
      return ''
  }
})

const handleBack = () => {
  emit('back')
  if (window.history.length > 1) {
    router.back()
  } else {
    router.replace({ name: 'Home' })
  }
}

const handlePtsClick = () => {
  router.push(props.ptsUrl)
}

const onSwitchTopTab = name => {
  if (name && name !== route.name) {
    router.replace({ name })
  }
}

const goToHistory = () => {
  const hasRewardsHistory = router.getRoutes().some(item => item.name === 'RewardsGameHistory')
  const targetName = hasRewardsHistory ? 'RewardsGameHistory' : 'GameHistory'
  router.push({ name: targetName })
}

const handleRechargeClick = () => {
  //routeRechargePage()
}

const fetchUserInfoForBalance = async () => {
  if (isFetchingUserInfo.value) return
  isFetchingUserInfo.value = true
  try {
    await store.REFRESH_BALANCE()
  } catch (error) {
    console.error('[CHeader] fetch user info failed:', error)
  } finally {
    isFetchingUserInfo.value = false
  }
}

onMounted(() => {
  fetchUserInfoForBalance()
})

watch(
  () => isTopTabLayout.value,
  () => {
    fetchUserInfoForBalance()
  }
)
</script>

<style scoped lang="less">
.c-header-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 101;
  background: #0d1218;
  color: #ffffff;
  padding: 0 10px;
  height: var(--mobile-header-height);

  .c-header-summary {
    background: #0d1218;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    height: 30px;

    &__notice {
      display: flex;
      align-items: center;
      font-size: 12px;
      gap: 4px;
      flex: 1;
      min-width: 0;
      font-family:
        PingFang SC,
        PingFang SC;
      overflow: hidden;
    }

    &__right {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 0 0 auto;
      min-width: max-content;

      .balance-text {
        font-size: 12px;
        padding: 4px 6px;
        border-radius: 4px 4px 4px 4px;
        background: #333333;
        display: inline-flex;
        align-items: center;
        white-space: nowrap;
        cursor: pointer;

        .balance-amount {
          color: #f0bc0f;
          line-height: 1;
          margin-left: 5px;
        }
      }
    }
  }

  .c-top-tab-header {
    position: sticky;
    top: 30px;
    z-index: 100;
    background: #0a0f1f;
    color: #ffffff;

    .notice-icon {
      flex: 0 0 auto;
    }

    .balance-unit {
      color: #ffffff;
      line-height: 1;
    }

    .balance-amount {
      color: #f0bc0f;
      line-height: 1;
      margin-left: 5px;
    }

    .history-btn {
      width: 24px;
      height: 24px;
      border: none;
      background: none;
      border-radius: 4px;
      color: #ffffff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
    }

    &__tabs {
      display: flex;
      align-items: center;
      height: 42px;
      gap: 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    }

    .top-tab-item {
      border: 0;
      background: transparent;
      color: #7f8aa0;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 0;
      height: 100%;
    }

    .top-tab-icon {
      width: 18px;
      height: 18px;
      display: block;
    }

    .top-tab-label {
      font-size: 14px;
      font-weight: 500;
      white-space: nowrap;
    }

    .top-tab-item.is-active {
      color: #ffffff;
    }
  }

  .c-header {
    &--out-layout {
      .c-header-left {
        margin-left: 10px;
      }
    }

    position: fixed;
    top: 30px;
    left: 0;
    right: 0;
    z-index: 100;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-image: url('@/assets/images/top_bg-DEOLogib.webp');

    &-left {
      display: flex;
      align-items: center;
      flex: 1;
      min-width: 0;
      height: 100%;
    }

    &-back {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      margin-right: 4px;
      cursor: pointer;
    }

    &-icon {
      font-size: 20px;
      color: #ffffff;
    }

    &-titles {
      display: flex;
      flex-direction: column;
      justify-content: center;
      min-width: 0;
      flex: 1;
    }

    &-main {
      font-size: 14px;
      color: #ffffff;
      font-family: CadillacTrimV1-SemiBold, sans-serif;
      font-weight: 600;
      line-height: 1.3;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: flex;
      align-items: center;
      gap: 6px;

      &--large {
        font-size: 16px;
        font-weight: 600;
      }
    }

    &-title-text {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    // 状态标签样式
    &-status {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      white-space: nowrap;
      flex-shrink: 0;
      font-family: CadillacTrimV1-SemiBold, sans-serif;
      font-weight: 600;
      border-radius: 10px;
      text-transform: uppercase;

      // Live状态 - 红色闪烁效果
      &--live {
        background: rgba(216, 45, 44, 0.2);
        color: #d82d2c;
        border: 1px solid rgba(216, 45, 44, 0.4);
        animation: live-pulse 2s ease-in-out infinite;
      }

      // Completed状态 - 灰色
      &--completed {
        background: rgba(106, 214, 128, 0.25);
        color: rgb(106, 214, 128);
        border: 1px solid rgba(136, 136, 136, 0.4);
      }

      // Scheduled状态 - 蓝色
      &--scheduled {
        background: rgba(0, 123, 255, 0.2);
        color: #007bff;
        border: 1px solid rgba(0, 123, 255, 0.4);
        display: none;
      }
    }

    // Live状态闪烁点
    &-status-dot {
      width: 5px;
      height: 5px;
      border-radius: 50%;

      &--live {
        background: #d82d2c;
        animation: dot-blink 1s ease-in-out infinite;
      }

      &--completed {
        background: rgb(106, 214, 128);
      }
    }

    &-sub {
      font-size: 12px;
      color: #888;
      line-height: 1.3;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &-right {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      min-width: 40px;
      height: 100%;
      padding-right: 10px;
    }

    &-pts {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 4px;

      &-icon {
        width: 24px;
        height: 24px;
        object-fit: contain;
      }
    }
  }
}

// Live状态闪烁动画
@keyframes live-pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }
}

@keyframes dot-blink {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.5;
    transform: scale(0.8);
  }
}
</style>
